/* eslint-disable import/no-extraneous-dependencies */
import BorderRadius from '@omni/kit/theming/BorderRadius';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import React, { useEffect, useRef } from 'react';
import { Platform, StyleSheet, Text, TextInput, View } from 'react-native';
import {
  CodeField,
  Cursor,
  useClearByFocusCell,
} from 'react-native-confirmation-code-field';

const CELL_COUNT = 6;

const KitVerifyCodeInput = ({
  setValue,
  value,
  children,
}: {
  setValue: (value: string) => void;
  value: string;
  children?: React.ReactNode;
}): JSX.Element => {
  const inputRef = useRef<TextInput>(null);
  const [codeFieldProps, getCellOnLayoutHandler] = useClearByFocusCell({
    value,
    setValue,
  });

  useEffect(() => {
    const timeout = setTimeout(() => {
      inputRef.current?.focus();
    }, 300); // Delay ensures view is fully mounted

    return () => clearTimeout(timeout);
  }, []);

  return (
    <View>
      <View
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
        }}
      >
        <CodeField
          ref={inputRef}
          {...codeFieldProps}
          caretHidden={false}
          value={value}
          cellCount={CELL_COUNT}
          /**
           * keyboardType:
           * Must use 'numeric' keyboard type.
           *
           * In testing 'number-pad' does not support
           * keyboard-based code suggestions on Android
           */
          keyboardType='numeric'
          textInputStyle={
            Platform.OS === 'web'
              ? ({ outlineStyle: 'none' } as any)
              : undefined
          }
          /**
           * autoComplete:
           *
           * In testing, Android will show keyboard-based code suggestions
           * if you either wait about 60 seconds between code attempts or
           * make sure to receive the SMS code prior to opening the screen with
           * the CodeField component.
           */
          autoComplete={Platform.OS === 'android' ? 'sms-otp' : 'one-time-code'}
          textContentType={Platform.OS === 'ios' ? 'oneTimeCode' : undefined}
          importantForAutofill={Platform.OS === 'android' ? 'yes' : undefined}
          onChangeText={setValue}
          renderCell={({ index, symbol, isFocused }) => (
            <Text
              key={index}
              onLayout={getCellOnLayoutHandler(index)}
              style={[styles.cell, isFocused && styles.focusCell]}
            >
              {symbol || (isFocused ? <Cursor /> : null)}
            </Text>
          )}
          testID='ConfirmationCodeField'
        />
      </View>

      {children}
    </View>
  );
};

const styles = StyleSheet.create({
  cell: {
    backgroundColor: Colors.N75,
    borderRadius: BorderRadius.m,
    color: Colors.N900,
    fontSize: 32,
    height: 54,
    overflow: 'hidden',
    paddingTop: Platform.OS === 'ios' ? 8 : 5,
    textAlign: 'center',
    width: 44,
    marginRight: Spacing.m,
  },
  focusCell: { color: Colors.brand },
});

export default KitVerifyCodeInput;
